import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';

// Assurez-vous d'avoir importé le plugin DrawSVG si ce n'est pas déjà fait
// Nécessite GSAP Club GreenSock ou une licence business
// import { DrawSVGPlugin } from 'gsap/DrawSVGPlugin';
// gsap.registerPlugin(DrawSVGPlugin);

const LogoSVG: React.FC = () => {
  const svgRef = useRef<SVGSVGElement>(null);
  const animationRef = useRef<any>(null);
  const intervalRef = useRef<any>(null);
  const shakeIntervalRef = useRef<any>(null);

  // Fonction pour créer et lancer l'animation
  const playLogoAnimation = () => {
    if (!svgRef.current) return;
    
    // Nettoyer toute animation précédente
    if (animationRef.current) {
      animationRef.current.kill();
    }
    
    if (shakeIntervalRef.current) {
      clearInterval(shakeIntervalRef.current);
    }
    
    // Récupérer tous les tracés
    const paths = svgRef.current.querySelectorAll('path');
    
    // Créer une nouvelle timeline GSAP
    const tl = gsap.timeline();
    
    // Référencer la timeline pour pouvoir la tuer plus tard
    animationRef.current = tl;
    
    // Configurer les chemins pour l'animation de trait
    paths.forEach(path => {
      // Obtenir la longueur du tracé
      const length = path.getTotalLength();
      
      // Configurer le chemin pour l'animation de dessin
      gsap.set(path, {
        strokeDasharray: length,
        strokeDashoffset: length,
        opacity: 1,
        fill: 'rgba(0, 0, 0, 0)',  // Commencer avec un remplissage transparent
        stroke: '#000000',
        strokeWidth: 5,
        x: 0,
        y: 0
      });
    });
    
    // Animer chaque tracé individuellement avec un délai plus prononcé
    // pour donner l'impression d'écriture manuelle
    paths.forEach((path, index) => {
      tl.to(path, {
        duration: 0.8 + Math.random() * 0.5, // Durée variable pour un effet plus naturel
        strokeDashoffset: 0,
        ease: "power1.inOut",
        delay: 0.05, // Petit délai entre chaque tracé
      }, index * 0.15); // Stagger plus prononcé entre chaque lettre
    });
    
    // Une fois tous les tracés dessinés, animer le remplissage
    tl.to(paths, {
      duration: 0.6,
      fill: '#000000',
      stagger: 0.03, // Léger décalage pour le remplissage aussi
      ease: "power1.inOut"
    }, "+=0.2"); // Attendre un peu avant de commencer le remplissage
    
    // Ajouter un léger tremblement pendant l'écriture pour simuler la main humaine
    const shake = () => {
      paths.forEach(path => {
        if (Math.random() > 0.7) { // Seulement certains tracés tremblent à chaque frame
          gsap.to(path, {
            duration: 0.05,
            x: Math.random() * 0.5 - 0.25,
            y: Math.random() * 0.5 - 0.25,
            onComplete: () => {
              gsap.to(path, {
                duration: 0.05,
                x: 0,
                y: 0
              });
            }
          });
        }
      });
    };
    
    // Créer un intervalle pour le tremblement pendant l'animation
    shakeIntervalRef.current = setInterval(shake, 100);
    
    // Arrêter le tremblement une fois l'animation terminée
    tl.call(() => clearInterval(shakeIntervalRef.current), [], "+=0.5");
  };

  useEffect(() => {
    // Lancer l'animation initiale
    playLogoAnimation();
    
    // Configurer l'intervalle pour relancer l'animation toutes les 7 secondes
    intervalRef.current = setInterval(() => {
      // D'abord, faire disparaître le logo actuel
      const paths = svgRef.current?.querySelectorAll('path');
      if (paths) {
        const fadeTl = gsap.timeline({
          onComplete: playLogoAnimation // Relancer l'animation après la disparition
        });
        
        // Faire disparaître le remplissage d'abord
        fadeTl.to(paths, {
          duration: 0.4,
          fill: 'rgba(0, 0, 0, 0)',
          stagger: 0.02,
          ease: "power1.inOut"
        });
        
        // Puis faire disparaître les traits
        fadeTl.to(paths, {
          duration: 0.6,
          strokeDashoffset: (i, path) => path.getTotalLength(),
          stagger: 0.03,
          ease: "power1.inOut"
        });
      }
    }, 10000); // Toutes les 7 secondes
    
    // Nettoyer les intervalles et animations à la destruction du composant
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (shakeIntervalRef.current) {
        clearInterval(shakeIntervalRef.current);
      }
      if (animationRef.current) {
        animationRef.current.kill();
      }
    };
  }, []);

  return (
    <svg 
      ref={svgRef}
      width="200" 
      height="88" 
      viewBox="0 0 500 219" 
      xmlns="http://www.w3.org/2000/svg"
      className="h-full w-auto"
    >
      <g transform="translate(0.000000,219.000000) scale(0.100000,-0.100000)" fill="#000000" stroke="#000000" strokeWidth="5">
        <path d="M2000 2060 c-16 -16 -20 -28 -15 -51 4 -16 10 -162 14 -323 7 -312 9 -326 57 -326 27 0 52 23 59 53 3 12 12 67 20 122 23 149 32 174 60 181 47 12 122 -23 145 -67 6 -11 15 -72 21 -136 12 -138 18 -153 62 -153 50 0 57 14 57 114 0 191 -34 278 -131 331 -49 27 -62 30 -140 28 l-86 -1 -11 93 c-6 52 -16 107 -24 122 -17 36 -59 42 -88 13z"/>
        <path d="M1660 1955 c-8 -10 -15 -51 -18 -101 l-5 -84 -38 -10 c-85 -23 -73 -120 16 -120 39 0 35 10 60 -145 25 -162 71 -228 157 -227 45 1 107 36 134 77 21 32 11 72 -21 87 -20 9 -31 6 -70 -16 -25 -15 -49 -25 -54 -22 -9 6 -38 153 -43 221 l-3 40 58 5 c60 6 87 24 87 60 0 34 -24 50 -87 56 l-63 7 -1 56 c0 31 -5 72 -12 91 -10 30 -17 35 -48 38 -24 2 -40 -2 -49 -13z"/>
        <path d="M3165 1893 c-95 -25 -161 -185 -105 -256 11 -14 64 -54 117 -89 54 -36 105 -73 113 -82 13 -16 12 -20 -5 -32 -30 -22 -63 -17 -131 21 -68 38 -91 37 -114 -6 -15 -27 -5 -53 33 -85 103 -87 245 -84 321 6 30 36 35 94 12 138 -21 39 -82 94 -153 136 -70 41 -93 68 -86 96 14 54 63 48 93 -10 28 -55 55 -68 89 -42 20 15 26 28 25 55 -1 41 -48 121 -80 136 -28 12 -103 21 -129 14z"/>
        <path d="M783 1839 c-18 -5 -47 -24 -67 -42 l-34 -33 -41 38 c-48 45 -94 50 -154 18 -28 -15 -42 -18 -57 -10 -27 15 -66 12 -80 -5 -10 -12 -10 -41 -2 -138 7 -67 14 -158 17 -202 4 -44 13 -90 21 -102 16 -26 65 -31 83 -10 6 8 15 69 21 136 5 67 13 134 16 149 10 39 33 82 45 82 19 0 50 -90 71 -203 11 -62 27 -122 34 -134 16 -26 65 -31 83 -9 6 7 13 65 16 131 4 97 9 127 27 164 13 25 28 47 35 49 20 6 40 -47 57 -153 10 -55 23 -111 29 -125 15 -31 52 -38 78 -14 54 49 -10 348 -85 398 -31 20 -76 26 -113 15z"/>
        <path d="M1201 1839 c-49 -15 -95 -53 -135 -114 -71 -106 -71 -186 -1 -291 38 -58 83 -84 147 -84 54 0 98 26 138 82 17 24 34 42 38 41 3 -1 13 -32 22 -70 18 -80 44 -103 94 -84 l29 12 -7 72 c-3 40 -19 130 -36 201 -33 145 -49 170 -130 211 -56 28 -114 37 -159 24z m100 -129 c11 -6 19 -21 19 -35 0 -35 -27 -109 -58 -160 -40 -66 -61 -69 -99 -12 -71 104 35 262 138 207z"/>
        <path d="M2914 1815 c-7 -19 -14 -37 -14 -40 0 -19 -115 -245 -123 -242 -6 2 -37 46 -69 97 -120 191 -151 217 -198 170 -23 -23 -24 -33 -9 -63 22 -41 171 -282 197 -315 l25 -34 -27 -87 c-33 -110 -34 -166 -5 -182 29 -15 64 1 82 38 8 15 24 60 37 98 12 39 63 162 113 274 49 113 92 223 95 246 5 50 -15 75 -59 75 -25 0 -33 -6 -45 -35z"/>
        <path d="M3220 975 c-22 -57 -2 -603 23 -651 33 -63 104 -105 181 -107 66 -1 96 15 96 51 0 39 -15 50 -83 64 -86 18 -86 17 -97 339 -7 178 -14 281 -23 298 -9 20 -21 27 -50 29 -32 3 -38 0 -47 -23z"/>
        <path d="M2100 902 c-21 -17 -24 -35 -41 -213 -24 -266 -30 -309 -41 -303 -14 9 -112 210 -153 314 -55 143 -65 160 -87 160 -62 0 -78 -34 -134 -273 -54 -230 -54 -231 -64 -221 -15 17 -150 465 -150 499 0 2 -9 9 -21 15 -29 16 -75 -4 -83 -35 -7 -29 34 -190 114 -444 82 -261 108 -311 161 -311 19 0 34 8 44 23 8 12 35 110 60 217 25 107 50 212 56 233 l10 38 88 -173 c137 -270 159 -300 211 -283 11 4 27 19 35 35 30 57 92 648 74 697 -7 18 -37 43 -52 43 -3 0 -15 -8 -27 -18z"/>
        <path d="M3672 859 c-30 -11 -42 -47 -42 -123 0 -66 -11 -86 -45 -86 -12 0 -29 -8 -38 -18 -37 -40 -8 -102 48 -102 19 0 37 -5 40 -10 4 -6 12 -50 20 -98 16 -110 30 -159 54 -199 40 -64 103 -82 174 -48 64 30 90 73 73 122 -11 35 -41 38 -92 8 -28 -16 -50 -23 -55 -18 -11 11 -34 120 -44 206 l-7 57 52 0 c72 0 90 12 90 60 0 37 -2 40 -37 49 -21 6 -52 11 -69 11 l-31 0 -7 77 c-4 42 -11 84 -16 93 -12 22 -39 29 -68 19z"/>
        <path d="M4672 763 c-18 -8 -44 -29 -58 -46 l-25 -30 -23 22 c-26 25 -68 27 -90 5 -20 -19 -20 -23 4 -114 11 -41 27 -129 36 -195 9 -66 20 -128 25 -137 12 -22 62 -24 82 -4 10 10 16 50 20 129 5 103 9 123 37 182 38 80 59 91 136 74 46 -10 56 -10 78 5 51 33 28 89 -46 111 -66 19 -134 19 -176 -2z"/>
        <path d="M2381 729 c-114 -34 -213 -204 -182 -311 19 -64 74 -142 114 -161 48 -23 114 -22 155 3 18 11 47 40 64 65 17 24 35 42 38 40 4 -3 11 -26 15 -51 14 -96 43 -126 99 -104 l29 11 -7 72 c-3 40 -19 130 -36 201 -33 145 -49 170 -130 211 -56 28 -114 37 -159 24z m100 -129 c11 -6 19 -21 19 -35 0 -36 -28 -114 -58 -162 -40 -64 -61 -66 -99 -10 -71 104 35 262 138 207z"/>
        <path d="M2828 672 c-90 -83 -125 -190 -94 -293 33 -116 102 -172 211 -173 49 0 75 7 140 37 43 21 83 43 87 50 17 26 8 66 -17 83 -24 16 -29 15 -107 -16 -79 -31 -134 -40 -157 -25 -9 5 -51 77 -51 87 0 2 60 3 133 4 136 1 160 6 205 47 40 37 23 96 -47 160 -61 55 -98 70 -182 75 l-77 4 -44 -40z m177 -96 c59 -25 44 -39 -35 -34 -79 6 -89 11 -70 33 16 19 60 19 105 1z"/>
        <path d="M4071 663 c-65 -68 -94 -131 -95 -207 -2 -132 76 -232 192 -250 51 -8 102 5 189 46 69 32 92 63 73 98 -22 41 -44 42 -130 10 -44 -17 -93 -32 -110 -32 -35 -1 -74 30 -88 71 l-9 26 131 1 c148 0 195 14 222 65 12 25 12 33 0 62 -7 19 -31 51 -52 71 -60 58 -108 79 -197 84 l-78 5 -48 -50z m184 -87 c19 -8 35 -20 35 -25 0 -14 -90 -14 -125 -1 -23 9 -25 13 -15 25 16 19 61 19 105 1z"/>
      </g>
    </svg>
  );
};

export default LogoSVG; 